import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
} from '@/components/ui'
import { fetchReunioes, fetchReunioesDashboard, marcarReuniaoRealizada } from './api'

export function ReunioesPage() {
  const { canEdit } = useAuth()
  const qc = useQueryClient()

  const lista = useQuery({ queryKey: ['wi', 'reunioes'], queryFn: fetchReunioes })
  const dash = useQuery({
    queryKey: ['wi', 'reunioes', 'dashboard'],
    queryFn: fetchReunioesDashboard,
  })

  const mut = useMutation({
    mutationFn: marcarReuniaoRealizada,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wi', 'reunioes'] })
    },
  })

  const rows = lista.data || []
  const d = dash.data || {}

  return (
    <section className="hi-page">
      <PageHeader
        title="Reuniões de liderança"
        subtitle="Agenda e compliance semanal via Flow Intelligence."
        actions={
          <a className="btn btn-secondary" href={legacyAppUrl('#/reunioes-lideranca')}>
            Legado
          </a>
        }
      />

      {dash.data ? (
        <KpiGrid>
          <Kpi label="Total" value={String(d.total ?? rows.length)} />
          <Kpi label="Realizadas" value={String(d.realizadas ?? '—')} />
          <Kpi label="Pendentes" value={String(d.pendentes ?? '—')} />
          <Kpi
            label="Gestores sem reunião"
            value={String(
              Array.isArray(d.gestores_sem_reuniao) ? d.gestores_sem_reuniao.length : '—',
            )}
          />
        </KpiGrid>
      ) : null}

      <Section title="Reuniões" flush>
        {lista.isLoading ? <Muted>Carregando…</Muted> : null}
        {lista.isError ? (
          <Alert tone="danger">
            {lista.error instanceof ApiError ? lista.error.message : 'Falha ao listar reuniões.'}
          </Alert>
        ) : null}
        {!lista.isLoading && rows.length === 0 ? <Empty>Nenhuma reunião listada.</Empty> : null}
        {rows.length > 0 ? (
          <DataTable>
            <thead>
              <tr>
                <th>Título</th>
                <th>Tipo</th>
                <th>Data</th>
                <th>Status</th>
                {canEdit('reunioes-lideranca') ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{String(r.titulo || 'Reunião')}</td>
                  <td>{String(r.tipo || '—')}</td>
                  <td>
                    {r.data_hora || r.data
                      ? new Date(String(r.data_hora || r.data)).toLocaleString('pt-BR')
                      : '—'}
                  </td>
                  <td>
                    <StatusBadge
                      tone={
                        String(r.status).includes('realiz')
                          ? 'ativo'
                          : String(r.status).includes('pend')
                            ? 'pendente'
                            : 'inativo'
                      }
                    >
                      {String(r.status || '—')}
                    </StatusBadge>
                  </td>
                  {canEdit('reunioes-lideranca') ? (
                    <td>
                      {!String(r.status).includes('realiz') ? (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled={mut.isPending}
                          onClick={() => mut.mutate(r.id)}
                        >
                          Marcar realizada
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : null}
      </Section>
    </section>
  )
}
